import Icon from '@ant-design/icons';
import { message } from 'antd';
import classNames from 'classnames';
import { useEffect, useState } from 'react';

import DoneIcon from '@/components/Icon/svgs/check_circle.svg';
import RightSvg from '@/components/Icon/svgs/right.svg';
import UndoneIcon from '@/components/Icon/svgs/todo_undone.svg';
import LottieJumpLoading from '@/components/chat-app/components/LottieJumpLoading';
import { useModel } from '@/hooks/useModel';
import { useTranslation } from '@/i18n/client';
import type { IGroupUserInfo } from '@/service/friend';
import { getCircleMemberList } from '@/service/friend';

import styles from '../GroupMemberSelect/index.less';

interface IProps {
  className?: string;
  handleClose: () => void;
  selectedIds?: string[];
  onSelect: (member: IGroupUserInfo) => void;
  onConfirm?: () => void;
  confirmLoading?: boolean;
  circleRoute: string;
  // 控制弹窗打开状态
  open?: boolean;
  // 自定义标题
  title?: string;
  // 自定义确认按钮文案
  confirmText?: string;
}

const RemoveMemberSelect = (props: IProps): JSX.Element => {
  const {
    className,
    handleClose,
    onSelect,
    selectedIds = [],
    onConfirm,
    confirmLoading,
    circleRoute,
    open,
    title,
    confirmText
  } = props;

  const { t } = useTranslation();
  const { userProfile } = useModel('auth-model', (model) => ({ userProfile: model.userProfile }));

  const [memberList, setMemberList] = useState<IGroupUserInfo[]>();
  const [loading, setLoading] = useState<boolean>(false);

  const handleGetMemberList = () => {
    setLoading(true);

    getCircleMemberList({
      route: circleRoute,
      adminOnly: false
    })
      .then((res) => {
        if (res.data.code !== 0) {
          throw new Error(res.data.message || t('error.oops'));
        }

        const responseData = res.data.data.list;

        // 过滤掉自己
        const filteredData = responseData.filter((item) => item.id !== userProfile?.userId);

        setMemberList(filteredData);
      })
      .catch((e) => {
        message.error(e.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (circleRoute && open) {
      handleGetMemberList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [circleRoute, open]);

  const isSelected = (id: string) => selectedIds.includes(id);

  return (
    <div
      className={classNames(styles.container, className)}
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <div className={styles.headerContainer}>
        <div className={styles.backBtn} onClick={handleClose}>
          <Icon className={styles.backIcon} component={RightSvg} />
        </div>
        <div className={styles.title}>{title || t('circle.removeMember')}</div>
        <div className={styles.placeholder} />
      </div>

      <div className={styles.contentContainer}>
        <div className={styles.memberContainer}>
          <div className={styles.memberTitle}>{t('circle.members')}</div>
          {loading || memberList === undefined ? (
            <LottieJumpLoading className="!w-16 !h-16 mt-20" />
          ) : memberList.length > 0 ? (
            memberList.map((item) => (
              <div
                key={item.id}
                className={classNames(styles.memberItem, {
                  [styles.active]: isSelected(item.id)
                })}
                onClick={() => onSelect(item)}
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img alt={item.username} className={styles.avatar} src={item.avatar} />
                <div className="flex-1 w-0 flex flex-col">
                  <div className="text-sm leading-6 font-medium text-[#231309]">
                    {item.nickname || item.username}
                  </div>
                </div>
                <div className={styles.iconWrapper}>
                  {isSelected(item.id) ? (
                    <Icon className={styles.icon} component={DoneIcon} />
                  ) : (
                    <Icon
                      className={classNames(styles.icon, styles.undone)}
                      component={UndoneIcon}
                    />
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="text-center text-sm leading-6 text-[#756C5D] mt-10">
              {t('common.noResult')}
            </div>
          )}
        </div>
      </div>

      {/* 确认按钮 */}
      <div
        className={classNames(styles.createBtn, {
          [styles.disabled]: confirmLoading || selectedIds.length === 0
        })}
        onClick={() => {
          if (confirmLoading || selectedIds.length === 0) return;

          onConfirm?.();
        }}
      >
        {confirmLoading ? t('common.removing') : confirmText || t('common.remove')}
      </div>
    </div>
  );
};

export default RemoveMemberSelect;
